import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, Grid, InputAdornment, InputLabel, MenuItem, Paper,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography, Avatar, Tooltip,
  IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClassIcon from '@mui/icons-material/Class';
import TranslateIcon from '@mui/icons-material/Translate';
import EmailIcon from '@mui/icons-material/Email';
import authService from '../Auth/AuthService';
import PageShell from '../sales/common/PageShell';
import StatCard from '../sales/common/StatCard';
import EmptyState from '../sales/common/EmptyState';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Small info row used in the mobile card
const CardField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const SchoolPage = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);
  const DOC_URL = authService.DOC_URL;
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = authService.pathUrl('GET', '/schools');
      const data = await authService.getAuth(url);
      setSchools(
        data.map((d) => ({
          ...d,
          logoUrl: d.logoUrl ? DOC_URL + d.logoUrl : null,
        }))
      );
    } catch (error) {
      console.error('Error fetching schools:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchData();
  }, []);

  // ---------- Search + filter + pagination ----------
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('schoolName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return schools;
    const term = searchTerm.trim().toLowerCase();
    return schools.filter((school) => {
      let value = '';
      switch (filterField) {
        case 'schoolName':
          value = school.schoolName?.toLowerCase() ?? '';
          break;
        case 'principalName':
          value = school.principalName?.toLowerCase() ?? '';
          break;
        case 'contactPhone':
          value = school.contactPhone?.toLowerCase() ?? '';
          break;
        case 'totalStudents':
          value = String(school.totalStudents ?? '');
          break;
        default:
          value = '';
      }
      return value.includes(term);
    });
  }, [schools, searchTerm, filterField]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterField]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const totalStudents = schools.reduce((s, x) => s + (x.totalStudents || 0), 0);
    const totalTeachers = schools.reduce((s, x) => s + (x.totalTeachers || 0), 0);
    const active = schools.filter((s) => s.isActive).length;
    return {
      total: schools.length,
      active,
      totalStudents,
      totalTeachers,
    };
  }, [schools]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ---------- Desktop table ----------
  const renderTable = () => (
    <Paper
      elevation={0}
      sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
    >
      <TableContainer>
        <Table sx={{ minWidth: 950 }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  bgcolor: 'grey.50',
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <TableCell>School</TableCell>
              <TableCell>Principal</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((school) => (
              <TableRow
                key={school.id}
                hover
                sx={{ '&:last-child td': { borderBottom: 0 } }}
              >
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={school.logoUrl || undefined}
                      sx={{
                        bgcolor: 'primary.main',
                        width: 40,
                        height: 40,
                        fontWeight: 700,
                        '& img': { objectFit: 'cover' },
                      }}
                    >
                      {(school.schoolName || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {school.schoolName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {school.schoolType} · {school.boardAffiliation}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <PersonIcon fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{school.principalName || '—'}</Typography>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Stack spacing={0.25}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <PhoneIcon fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2">{school.contactPhone || '—'}</Typography>
                    </Stack>
                    {school.contactEmail && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <EmailIcon fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {school.contactEmail}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {school.addressDTO?.addressLine1 || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {[school.addressDTO?.city, school.addressDTO?.state]
                      .filter(Boolean)
                      .join(', ')}
                    {school.addressDTO?.pincode ? ` - ${school.addressDTO.pincode}` : ''}
                  </Typography>
                  {school.addressDTO?.latitude != null && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ mt: 0.3 }}
                    >
                      <LocationOnIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.disabled">
                        {school.addressDTO.latitude}, {school.addressDTO.longitude}
                      </Typography>
                    </Stack>
                  )}
                </TableCell>

                <TableCell>
                  <Grid container spacing={1.5} sx={{ maxWidth: 240 }}>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <GroupsIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        <Typography variant="caption" fontWeight={600}>
                          {school.totalStudents ?? 0}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Students
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PersonIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                        <Typography variant="caption" fontWeight={600}>
                          {school.totalTeachers ?? 0}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Teachers
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ClassIcon sx={{ fontSize: 14, color: '#0891b2' }} />
                        <Typography variant="caption" fontWeight={600}>
                          {school.totalClasses ?? 0}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Classes
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CalendarTodayIcon sx={{ fontSize: 14, color: '#ea580c' }} />
                        <Typography variant="caption" fontWeight={600}>
                          {school.establishedYear || '—'}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Established
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <TranslateIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
                        <Typography variant="caption" fontWeight={600} noWrap>
                          {school.mediumOfInstruction || '—'}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </TableCell>

                <TableCell>
                  <Chip
                    label={school.isActive ? 'Active' : 'Inactive'}
                    color={school.isActive ? 'success' : 'default'}
                    size="small"
                    variant={school.isActive ? 'filled' : 'outlined'}
                    icon={school.isActive ? <CheckCircleIcon /> : <BlockIcon />}
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 0.75 }}
                  >
                    Updated {formatDate(school.updatedAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
      />
    </Paper>
  );

  // ---------- Mobile card view ----------
  const renderCards = () => (
    <>
      <Grid container spacing={2}>
        {paginatedData.map((school) => (
          <Grid item xs={12} key={school.id}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all .15s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 6px 20px rgba(37,99,235,0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Avatar
                    src={school.logoUrl || undefined}
                    sx={{
                      bgcolor: 'primary.main',
                      width: 44,
                      height: 44,
                      fontWeight: 700,
                    }}
                  >
                    {(school.schoolName || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {school.schoolName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {school.schoolType} · {school.boardAffiliation}
                    </Typography>
                  </Box>
                  <Chip
                    label={school.isActive ? 'Active' : 'Inactive'}
                    color={school.isActive ? 'success' : 'default'}
                    size="small"
                    variant={school.isActive ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid item xs={6}>
                    <CardField label="Principal" value={school.principalName} />
                  </Grid>
                  <Grid item xs={6}>
                    <CardField label="Contact" value={school.contactPhone} />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 1.5,
                    borderRadius: 2,
                    mb: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      Address
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {school.addressDTO?.addressLine1 || '—'}
                    {school.addressDTO?.addressLine2 && `, ${school.addressDTO.addressLine2}`}
                    <br />
                    {[school.addressDTO?.city, school.addressDTO?.state]
                      .filter(Boolean)
                      .join(', ')}
                    {school.addressDTO?.pincode ? ` - ${school.addressDTO.pincode}` : ''}
                  </Typography>
                  {school.addressDTO?.latitude != null && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {school.addressDTO.latitude}, {school.addressDTO.longitude}
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={1.5}>
                  <Grid item xs={4}>
                    <CardField label="Students" value={school.totalStudents} />
                  </Grid>
                  <Grid item xs={4}>
                    <CardField label="Teachers" value={school.totalTeachers} />
                  </Grid>
                  <Grid item xs={4}>
                    <CardField label="Classes" value={school.totalClasses} />
                  </Grid>
                  <Grid item xs={6}>
                    <CardField label="Established" value={school.establishedYear} />
                  </Grid>
                  <Grid item xs={6}>
                    <CardField label="Medium" value={school.mediumOfInstruction} />
                  </Grid>
                </Grid>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Updated {formatDate(school.updatedAt)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 2 }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ border: 'none' }}
        />
      </Box>
    </>
  );

  // ---------- Loading ----------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ---------- Main render ----------
  return (
    <PageShell
      icon={<SchoolIcon />}
      title="Schools"
      subtitle="Browse and search registered schools"
      breadcrumbs={[{ label: 'Dashboard', to: '/' }, { label: 'Schools' }]}
      actions={
        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchData}
            disabled={loading}
            sx={{
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.5)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total Schools" value={stats.total} icon={<SchoolIcon />} color="#2563eb" />
        <StatCard label="Active" value={stats.active} icon={<CheckCircleIcon />} color="#059669" />
        <StatCard label="Total Students" value={stats.totalStudents} icon={<GroupsIcon />} color="#7c3aed" />
        <StatCard label="Total Teachers" value={stats.totalTeachers} icon={<PersonIcon />} color="#0891b2" />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Search by</InputLabel>
          <Select
            value={filterField}
            label="Search by"
            onChange={(e) => setFilterField(e.target.value)}
          >
            <MenuItem value="schoolName">School Name</MenuItem>
            <MenuItem value="principalName">Principal Name</MenuItem>
            <MenuItem value="contactPhone">Contact Phone</MenuItem>
            <MenuItem value="totalStudents">Total Students</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder={`Search ${
            filterField === 'schoolName'
              ? 'schools'
              : filterField === 'principalName'
              ? 'principals'
              : filterField === 'contactPhone'
              ? 'phone numbers'
              : 'student count'
          }...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Found <strong>{filteredData.length}</strong>{' '}
        {filteredData.length === 1 ? 'school' : 'schools'}
        {searchTerm && ` matching "${searchTerm}"`}
      </Typography>

      {/* Empty */}
      {filteredData.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<SchoolIcon />}
            title={schools.length === 0 ? 'No schools yet' : 'No matches'}
            subtitle={
              schools.length === 0
                ? 'Schools will appear here once they are registered.'
                : 'Try a different search term or filter field.'
            }
          />
        </Paper>
      ) : isTablet ? (
        renderCards()
      ) : (
        renderTable()
      )}
    </PageShell>
  );
};

export default SchoolPage;